/**
 * Oracle Cards Fallback Data
 * 
 * This is the single source of truth for offline/fallback oracle card data.
 * Categories MUST match the database exactly: physical, emotional, mental, relational, spiritual
 * 
 * IMPORTANT: This fallback should only be used when the database is unavailable.
 * The database (oracle_cards table) is the primary source of truth.
 */

export interface OracleCardData {
  id: string;
  title: string;
  category: 'physical' | 'emotional' | 'mental' | 'relational' | 'spiritual';
  message: string;
  keywords: string[];
  // Extended fields for rich display (may not be in DB)
  whatThisReflects?: string;
  healingInvitation?: string;
  affirmation?: string;
}

// Category display mapping for UI (plain text only, no emojis)
export const CATEGORY_DISPLAY_MAP: Record<string, string> = {
  physical: 'Physical',
  emotional: 'Emotional',
  mental: 'Mental',
  relational: 'Relational',
  spiritual: 'Spiritual',
};

export const EXPECTED_CARD_COUNT = 55;

/**
 * Fallback oracle cards data - matches database categories exactly
 * Only used when database fetch fails
 */
export const ORACLE_CARDS_FALLBACK: OracleCardData[] = [
  // THE TEMPLE OF FORM - Physical (11 cards)
  {
    id: 'fallback-1',
    title: 'The Whisper of the Body',
    category: 'physical',
    message: "The body doesn't speak in words - but it never lies. There's a quiet rhythm, a subtle message, a shift you keep feeling. Something is asking for your attention - not to scare you, but to realign you.",
    keywords: ['Body Wisdom', 'Inner Signal', 'Alignment', 'Softness'],
    whatThisReflects: 'The body often reflects what the mind has forgotten. This card may arise when sensations, symptoms, or stillness are showing you where you\'ve disconnected from your own inner signal.',
    healingInvitation: 'Ask: If this part of me could speak, what would it say? Then be still. Let the answer form as a feeling, image, or knowing.',
    affirmation: 'My body is not against me. It is the compass I come home to.',
  },
  {
    id: 'fallback-2',
    title: 'The Pattern on Repeat',
    category: 'physical',
    message: "Something keeps circling back - an action, a habit, a way of reacting. It may be subtle, automatic, or frustrating. But it's not a flaw. It's a flag on the field, asking you to pause, witness, and rewrite.",
    keywords: ['Patterns', 'Habits', 'Witnessing', 'Compassion'],
    whatThisReflects: 'When behaviours repeat - especially those that feel out of sync with our current self - they\'re often linked to unprocessed timelines or unmet needs.',
    healingInvitation: 'Ask: What am I really reaching for when I do this? What would I offer myself instead?',
    affirmation: 'Every pattern is a pathway. I meet mine with honesty and compassion.',
  },
  {
    id: 'fallback-3',
    title: 'The External Mirror',
    category: 'physical',
    message: "Look around. Your space is a mirror. What surrounds you may be silently shaping you. There is information in the mess, the order, the light, the weight of objects you haven't touched in months.",
    keywords: ['Environment', 'Reflection', 'Order', 'Care'],
    whatThisReflects: 'Our environment - from our room to our routines - reflects our internal state and often reinforces it.',
    healingInvitation: 'Choose one small area - a drawer, a bag, a bed. Ask: What part of me does this represent? What does it need?',
    affirmation: 'My space is a living symbol. I clear with care. I create with love.',
  },
  {
    id: 'fallback-4',
    title: 'The Body Remembers',
    category: 'physical',
    message: "Something old lives here - not in memory, but in muscle, skin, rhythm. You may not recall the moment, but your system does. This isn't about reliving the past, but about releasing the grip it still holds.",
    keywords: ['Somatic Memory', 'Release', 'Movement', 'Safety'],
    whatThisReflects: 'This card may appear when unresolved experiences are stored in the body as tension, fatigue, or pain.',
    healingInvitation: 'Place your hands on your body and breathe into that space. Ask: What memory or emotion might still live here?',
    affirmation: 'I release what was never mine to carry. My body is safe to let go.',
  },
  {
    id: 'fallback-5',
    title: 'The Shift in Rhythm',
    category: 'physical',
    message: "Your system is craving a different pace. A change in rhythm - faster or slower - could bring more alignment than any goal. This is a card of recalibration.",
    keywords: ['Rhythm', 'Pace', 'Alignment', 'Natural Flow'],
    whatThisReflects: 'Often overlooked, our natural rhythms - sleep, digestion, movement, rest - deeply affect our mental and emotional coherence.',
    healingInvitation: 'Ask: What is one physical rhythm I\'ve been ignoring? Where can I honour what my system is asking for?',
    affirmation: 'I move in rhythm with what I need now. Rest and energy live in my choices.',
  },
  {
    id: 'fallback-6',
    title: 'The Break in the Habit',
    category: 'physical',
    message: "A pattern is ready to be rewritten - not because you've failed, but because you've evolved. This card signals a point of interruption, a new neural pathway opening.",
    keywords: ['Evolution', 'Transformation', 'New Pathways', 'Choice'],
    whatThisReflects: 'This card arises when you\'re in the in-between - no longer fully in the old behaviour, but not yet anchored in the new.',
    healingInvitation: 'Ask: What belief fuels this habit? Then ask: What truth wants to replace it?',
    affirmation: 'My patterns are not fixed. I create new futures in every small choice.',
  },
  {
    id: 'fallback-7',
    title: 'The Anchor Point',
    category: 'physical',
    message: "There's something stabilising you - a small habit, object, space, or practice that helps you feel safe. This card reminds you to return to that anchor.",
    keywords: ['Grounding', 'Safety', 'Stability', 'Support'],
    whatThisReflects: 'Safety is the foundation of transformation. Without grounding, the system resists change.',
    healingInvitation: 'Ask: What makes me feel safe in my body and space? Create a ritual, corner, or cue that you can return to when dysregulated.',
    affirmation: 'I am grounded. I am supported. I choose what anchors me in peace.',
  },
  {
    id: 'fallback-8',
    title: 'The Body as Oracle',
    category: 'physical',
    message: "There is wisdom pulsing through your cells - a quiet knowing beyond logic. This card appears when the body is trying to speak as guide, not just vessel.",
    keywords: ['Intuition', 'Embodied Wisdom', 'Guidance', 'Trust'],
    whatThisReflects: 'Embodied intuition is a core principle of coherence. When the nervous system is calm, the body can become a channel for guidance.',
    healingInvitation: 'Before making a decision or taking action, ask: How does this feel in my body? Let your cells speak first.',
    affirmation: 'My body is not just flesh - it is knowing. I honour what it reveals.',
  },
  {
    id: 'fallback-9',
    title: 'The Energy Leak',
    category: 'physical',
    message: "Something is slowly draining you - not dramatically, but consistently. It might be an environment, routine, relationship, or way of being that once worked but now costs more than it gives.",
    keywords: ['Energy Management', 'Boundaries', 'Vitality', 'Discernment'],
    whatThisReflects: 'Energy depletion isn\'t always about doing too much. Sometimes it\'s about being misaligned with natural rhythms, boundaries, or authentic expression.',
    healingInvitation: 'Ask: Where do I feel energised versus depleted? What one thing could I shift to plug the leak?',
    affirmation: 'I guard my energy as sacred. I say yes to what enlivens me.',
  },
  {
    id: 'fallback-10',
    title: 'The Integrated Self',
    category: 'physical',
    message: "You're coming back into wholeness - not by being perfect, but by being present. This card signals a moment of coherence. Mind and body, habit and intention, rhythm and intuition - now move as one.",
    keywords: ['Integration', 'Wholeness', 'Coherence', 'Celebration'],
    whatThisReflects: 'Healing doesn\'t always look dramatic. Sometimes it feels like peace, clarity, or gentle consistency.',
    healingInvitation: 'Ask: What feels different now? Name three things you\'re doing differently - and celebrate them.',
    affirmation: 'I live in integrity with my Self. Each action reflects who I\'ve become.',
  },
  {
    id: 'fallback-11',
    title: 'The Embodied Light',
    category: 'physical',
    message: "You are the light in motion - not separate from the divine, but made of it. This is a card of embodiment, radiance, and physical purpose.",
    keywords: ['Embodied Light', 'Divine Expression', 'Radiance', 'Purpose'],
    whatThisReflects: 'When the physical self becomes an expression of coherence - when your habits, movements, and spaces align with your truth - you embody light.',
    healingInvitation: 'Ask: What does it mean to live as light in this body? Do something today that feels like a celebration of embodiment.',
    affirmation: 'My body is the temple of my light. I move through life as a living transmission.',
  },

  // THE HEART FIELD - Emotional (11 cards)
  {
    id: 'fallback-12',
    title: 'The Silent Heart',
    category: 'emotional',
    message: "Something feels out of reach - not because it isn't there, but because it's been wrapped in layers. This card appears when the emotional body is calling softly, waiting for safe space to speak.",
    keywords: ['Emotional Safety', 'Inner Listening', 'Heart Wisdom', 'Gentle Opening'],
    whatThisReflects: 'When emotions feel distant or muted, it\'s often a protective response to past overwhelm.',
    healingInvitation: 'Ask: What emotion might be trying to reach me? Create a quiet moment and simply listen to your heart without needing to fix or change anything.',
    affirmation: 'My heart is safe to feel. I create space for all emotions to be witnessed.',
  },
  {
    id: 'fallback-13',
    title: 'The Emotional Storm',
    category: 'emotional',
    message: "The weather inside you is intense right now - swirling, unpredictable, maybe overwhelming. This isn't chaos; it's clearing. Sometimes the emotional body needs to storm before it can settle.",
    keywords: ['Emotional Release', 'Processing', 'Intensity', 'Clearing'],
    whatThisReflects: 'Emotional storms often arise during periods of transition, loss, or when old patterns are ready to release.',
    healingInvitation: 'Ask: What is this storm trying to clear out of my system? Let yourself feel without judgment.',
    affirmation: 'I allow my emotions to move through me. After every storm comes clarity.',
  },
  {
    id: 'fallback-14',
    title: 'The Unfelt Feeling',
    category: 'emotional',
    message: "There may be something you haven't yet named - a buried ache, a paused grief, a love unspoken. This card arises to honour the unacknowledged.",
    keywords: ['Unexpressed Emotion', 'Presence', 'Honouring', 'Sacred Feeling'],
    whatThisReflects: 'Emotions that remain unexpressed can become embedded in the system, showing up through mood, avoidance, or projection.',
    healingInvitation: 'Ask: What feeling have I not made space for? Offer it a place to sit beside you.',
    affirmation: 'I honour all that lives within me. Every emotion has a reason.',
  },
  {
    id: 'fallback-15',
    title: 'The Trigger',
    category: 'emotional',
    message: "Something stirred you - quickly, deeply, unexpectedly. This card doesn't ask you to bypass it. It asks you to explore it. What core truth or old wound has been touched?",
    keywords: ['Triggers', 'Curiosity', 'Compassion', 'Healing'],
    whatThisReflects: 'Triggers reveal unresolved emotion, often connected to past imprints. They are not flaws. They are flags.',
    healingInvitation: 'Ask: What part of me needed protection in that moment? Offer it compassion instead of control.',
    affirmation: 'I explore my reactions with kindness. Every trigger is a teacher.',
  },
  {
    id: 'fallback-16',
    title: 'The Emotional Compass',
    category: 'emotional',
    message: "Your feelings are information, not instructions. This card appears when you're learning to use emotions as guidance rather than being hijacked by them.",
    keywords: ['Emotional Intelligence', 'Inner Guidance', 'Self-Regulation', 'Wisdom'],
    whatThisReflects: 'Emotional maturity involves developing a new relationship with feelings - seeing them as messengers carrying important data.',
    healingInvitation: 'Ask: What are my emotions trying to tell me right now? Practice observing your feelings with curiosity rather than judgment.',
    affirmation: 'My emotions are wise messengers. I listen to their guidance without being controlled by them.',
  },
  {
    id: 'fallback-17',
    title: 'The Grief That Liberates',
    category: 'emotional',
    message: "There's a sadness that needs to move through you - not to break you, but to free you. This grief may be for what was lost, what never was, or what must be released.",
    keywords: ['Grief', 'Release', 'Honouring Loss', 'Emotional Clearing'],
    whatThisReflects: 'Grief is love with nowhere to go. It\'s also the emotion that clears space for new love, new life, new possibilities.',
    healingInvitation: 'Ask: What loss am I still carrying? Create a ritual to honour what was and release what no longer serves.',
    affirmation: 'My grief is sacred water that clears the way for new growth.',
  },
  {
    id: 'fallback-18',
    title: 'The Joy Beneath',
    category: 'emotional',
    message: "Beneath the layers of protection, worry, or numbness - there is a joy that belongs to you. This card invites you to dig for it, to make space for lightness.",
    keywords: ['Joy', 'Lightness', 'Authenticity', 'Emotional Freedom'],
    whatThisReflects: 'Joy can feel vulnerable when we\'ve been hurt. Sometimes we unconsciously suppress it to stay safe.',
    healingInvitation: 'Ask: What brings me genuine joy? Allow yourself to experience it without guilt or fear.',
    affirmation: 'I deserve to feel joy. I welcome lightness into my life.',
  },
  {
    id: 'fallback-19',
    title: 'The Emotional Alchemy',
    category: 'emotional',
    message: "You're in the process of transforming lead into gold - taking heavy, difficult emotions and discovering their hidden gifts. Every feeling, even the painful ones, carries medicine.",
    keywords: ['Emotional Alchemy', 'Transformation', 'Conscious Feeling', 'Hidden Gifts'],
    whatThisReflects: 'Emotional alchemy is the art of working consciously with feelings to extract their wisdom and energy.',
    healingInvitation: 'Ask: What difficult emotion am I experiencing, and what gift might it be offering?',
    affirmation: 'I transform my emotions through conscious presence. Every feeling carries hidden wisdom.',
  },
  {
    id: 'fallback-20',
    title: 'The Forgiveness Portal',
    category: 'emotional',
    message: "Forgiveness is not about condoning what happened. It's about freeing yourself from carrying it. This card signals that a doorway to release is opening.",
    keywords: ['Forgiveness', 'Freedom', 'Release', 'Self-Compassion'],
    whatThisReflects: 'Unforgiveness keeps us energetically bound to the past. Forgiveness is a gift we give ourselves.',
    healingInvitation: 'Ask: What am I ready to forgive - in others or in myself? Write a letter you never send.',
    affirmation: 'I forgive to set myself free. I release the weight of the past.',
  },
  {
    id: 'fallback-21',
    title: 'The Open Heart',
    category: 'emotional',
    message: "You're not just feeling - you're flowing. This card arises when emotional availability, self-trust, and vulnerability open new pathways of love, empathy, and connection.",
    keywords: ['Open Heart', 'Vulnerability', 'Emotional Availability', 'Love'],
    whatThisReflects: 'An open heart doesn\'t mean no boundaries. It means spacious presence. This card reflects healthy emotional availability.',
    healingInvitation: 'Ask: What helps my heart feel safe to stay open? Let that be your ritual.',
    affirmation: 'My heart is open and protected. I let love move through me freely.',
  },
  {
    id: 'fallback-22',
    title: 'The Emotional Self as Guide',
    category: 'emotional',
    message: "You are now attuned to the wisdom of feeling. This card reflects emotional maturity - the ability to listen, sense, respond, and stay grounded. Emotion is no longer just reaction. It is revelation.",
    keywords: ['Emotional Intelligence', 'Guidance', 'Maturity', 'Integration'],
    whatThisReflects: 'This card signals the full integration of the emotional self - where feelings become feedback, not fate.',
    healingInvitation: 'Ask: How do my emotions guide me toward truth? Write what arises.',
    affirmation: 'I trust my emotional intelligence. It leads me back to myself.',
  },

  // THE MIND MIRROR - Mental (11 cards)
  {
    id: 'fallback-23',
    title: 'The Loudest Voice',
    category: 'mental',
    message: "A thought is repeating - looping, narrating, insisting it's true. This card arises when the mind is on high volume. You don't have to believe everything you think.",
    keywords: ['Thought Patterns', 'Inner Critic', 'Awareness', 'Choice'],
    whatThisReflects: 'Repetitive thoughts often serve as protection or control mechanisms. They\'re trying to keep you safe, but may be outdated.',
    healingInvitation: 'Ask: What is this thought trying to protect me from? Thank it, then choose a new narrative.',
    affirmation: 'I am not my thoughts. I can observe them and choose which to follow.',
  },
  {
    id: 'fallback-24',
    title: 'The Mental Maze',
    category: 'mental',
    message: "You're stuck in analysis paralysis - thinking about thinking about thinking. This card appears when the mind has become a maze instead of a tool.",
    keywords: ['Overthinking', 'Analysis Paralysis', 'Mental Loops', 'Body Wisdom'],
    whatThisReflects: 'Overthinking often arises when we try to think our way through emotional or energetic challenges.',
    healingInvitation: 'Ask: What am I trying to figure out that might be better felt, trusted, or experienced? Drop into your body for one minute.',
    affirmation: 'My mind is a tool, not my master. I know when to think and when to trust.',
  },
  {
    id: 'fallback-25',
    title: 'The Observer Self',
    category: 'mental',
    message: "There's a part of you that watches your thoughts, emotions, and reactions without being swept away by them. This card signals the awakening of your witness consciousness.",
    keywords: ['Observer Self', 'Witness Consciousness', 'Detachment', 'Higher Perspective'],
    whatThisReflects: 'The observer self represents a higher level of consciousness that can witness mental and emotional patterns without being identified with them.',
    healingInvitation: 'Ask: Who is the one watching my thoughts? Practice identifying with the observer rather than the observed.',
    affirmation: 'I am not my thoughts or emotions. I am the awareness that witnesses them.',
  },
  {
    id: 'fallback-26',
    title: 'The Belief Beneath',
    category: 'mental',
    message: "Beneath your conscious thoughts lies a deeper layer of belief - assumptions about yourself, life, and what's possible. This card invites you to excavate what's been running in the background.",
    keywords: ['Core Beliefs', 'Subconscious', 'Self-Discovery', 'Reprogramming'],
    whatThisReflects: 'Our deepest beliefs often formed before we had critical thinking. They run like invisible software, shaping our experience.',
    healingInvitation: 'Ask: What belief about myself or life might be operating beneath this situation?',
    affirmation: 'I can update my beliefs. What I once believed no longer has to limit me.',
  },
  {
    id: 'fallback-27',
    title: 'The Reframe',
    category: 'mental',
    message: "The same situation can look completely different depending on the lens you use. This card invites you to try on a new perspective - not to deny reality, but to expand it.",
    keywords: ['Perspective', 'Reframing', 'Mental Flexibility', 'New Lens'],
    whatThisReflects: 'Cognitive flexibility - the ability to see situations from multiple angles - is a key skill of mental health and creativity.',
    healingInvitation: 'Ask: What\'s another way to see this situation? What would a trusted mentor or future self say?',
    affirmation: 'I choose how I see my experiences. I can find meaning in any situation.',
  },
  {
    id: 'fallback-28',
    title: "The Beginner's Mind",
    category: 'mental',
    message: "What if you knew nothing about this situation? This card invites you to approach a familiar challenge with fresh curiosity instead of old assumptions.",
    keywords: ["Beginner's Mind", 'Fresh Perspective', 'Curiosity', 'Not-Knowing'],
    whatThisReflects: 'Expertise can become a limitation when it prevents us from seeing new solutions. Beginner\'s mind opens us to deeper knowing.',
    healingInvitation: 'Ask: What would I see if I looked at this situation for the first time? Practice not knowing as a doorway to discovery.',
    affirmation: 'I approach life with fresh eyes. Not-knowing opens me to new possibilities.',
  },
  {
    id: 'fallback-29',
    title: 'The Mental Clarity',
    category: 'mental',
    message: "The fog is lifting. Confusion is giving way to clarity. This card signals a moment when the mind becomes an ally rather than an obstacle.",
    keywords: ['Clarity', 'Focus', 'Mental Peace', 'Insight'],
    whatThisReflects: 'Mental clarity often follows periods of confusion or overwhelm. It\'s the reward for staying present through the uncertainty.',
    healingInvitation: 'Ask: What has become clear to me? Write down your insights before they fade.',
    affirmation: 'My mind is clear and focused. I trust the clarity that comes to me.',
  },
  {
    id: 'fallback-30',
    title: 'The Creative Mind',
    category: 'mental',
    message: "Your mind is ready to create, innovate, and imagine. This card signals a time of mental fertility - ideas want to be born through you.",
    keywords: ['Creativity', 'Innovation', 'Imagination', 'Mental Flow'],
    whatThisReflects: 'Creative thinking is a natural function of the mind when it\'s not burdened by excessive stress or rigid patterns.',
    healingInvitation: 'Ask: What creative project or idea is calling me? Give it even 10 minutes of your attention today.',
    affirmation: 'My mind is a creative force. Ideas flow through me with ease.',
  },
  {
    id: 'fallback-31',
    title: 'The Intuitive Mind',
    category: 'mental',
    message: "Your rational mind is learning to dance with your intuitive mind. This card appears when you're developing the capacity to integrate logical analysis with intuitive knowing.",
    keywords: ['Intuitive Intelligence', 'Integration', 'Holistic Thinking', 'Inner Knowing'],
    whatThisReflects: 'True intelligence isn\'t just rational or just intuitive - it\'s the integration of both.',
    healingInvitation: 'Ask: What does my intuitive mind know about this situation? How can I honour both my rational analysis and my intuitive insights?',
    affirmation: 'I integrate rational thinking with intuitive knowing. Both are valid forms of intelligence.',
  },
  {
    id: 'fallback-32',
    title: 'The Quiet Mind',
    category: 'mental',
    message: "Beneath the chatter, there is silence. This card invites you to find the stillness beneath your thoughts - the awareness that exists in the gaps between mental noise.",
    keywords: ['Mental Stillness', 'Inner Peace', 'Meditation', 'Presence'],
    whatThisReflects: 'The quiet mind isn\'t about stopping thoughts - it\'s about resting in the awareness that holds them.',
    healingInvitation: 'Ask: Can I notice the space between my thoughts? Practice resting in awareness for just one minute.',
    affirmation: 'I am the stillness beneath my thoughts. Peace is always available to me.',
  },
  {
    id: 'fallback-33',
    title: 'The Integrated Mind',
    category: 'mental',
    message: "Your mind has become a trusted ally - balanced, flexible, and wise. This card signals the integration of all mental faculties: analysis, intuition, creativity, and stillness.",
    keywords: ['Mental Integration', 'Wisdom', 'Balance', 'Mastery'],
    whatThisReflects: 'Mental mastery isn\'t about controlling thoughts - it\'s about developing a healthy relationship with the mind.',
    healingInvitation: 'Ask: How has my relationship with my mind evolved? Celebrate your mental growth.',
    affirmation: 'My mind is my ally. I think with clarity, create with freedom, and rest in awareness.',
  },

  // THE REFLECTED SELF - Relational (11 cards)
  {
    id: 'fallback-34',
    title: 'The Mirror in the Room',
    category: 'relational',
    message: "Someone in your life is reflecting something back to you - not to shame, but to show. This card reminds you that relationships are sacred mirrors.",
    keywords: ['Relationship Mirror', 'Self-Reflection', 'Projection', 'Inner Work'],
    whatThisReflects: 'The Mirror of Meaning™ principle suggests that our external experiences, especially relationships, reflect our internal landscape.',
    healingInvitation: 'Ask: What quality do I see in this person that might also live within me? How is this relationship teaching me about myself?',
    affirmation: 'I see relationships as mirrors. I learn about myself through how I relate to others.',
  },
  {
    id: 'fallback-35',
    title: 'The Attachment Pattern',
    category: 'relational',
    message: "How you connect is shaped by how you were connected to. This card invites you to explore your attachment style - not to judge it, but to understand it.",
    keywords: ['Attachment', 'Patterns', 'Connection', 'Healing'],
    whatThisReflects: 'Attachment patterns formed in childhood continue to influence adult relationships until consciously examined.',
    healingInvitation: 'Ask: What is my pattern when I feel close to someone? What do I need to feel secure in connection?',
    affirmation: 'I understand my attachment patterns. I can create new ways of connecting.',
  },
  {
    id: 'fallback-36',
    title: 'The Boundary Teacher',
    category: 'relational',
    message: "Someone is teaching you about boundaries - either by crossing yours or by modeling healthy ones. This card appears when boundary lessons are active.",
    keywords: ['Boundaries', 'Self-Worth', 'Healthy Limits', 'Self-Respect'],
    whatThisReflects: 'Boundary challenges in relationships often reflect our relationship with ourselves.',
    healingInvitation: 'Ask: What boundary do I need to set or strengthen? Practice saying no to something small as preparation for bigger boundaries.',
    affirmation: 'I set loving boundaries. I honour my needs while respecting others.',
  },
  {
    id: 'fallback-37',
    title: 'The Heart Healer',
    category: 'relational',
    message: "You have a gift for holding space in relationships - helping others feel seen, heard, and accepted. This card recognises your capacity to create healing through conscious presence.",
    keywords: ['Relationship Healer', 'Holding Space', 'Emotional Safety', 'Healing Presence'],
    whatThisReflects: 'Some people naturally create safe emotional containers in relationships, allowing others to heal and grow.',
    healingInvitation: 'Ask: How do I naturally help others feel safe and seen? Where is this gift needed in my community?',
    affirmation: 'I create healing through conscious presence. My authentic connection helps others feel safe.',
  },
  {
    id: 'fallback-38',
    title: 'The Relationship Alchemist',
    category: 'relational',
    message: "You have the ability to transform relationship challenges into deeper connection. This card appears when conflict or tension is actually an opportunity for greater intimacy.",
    keywords: ['Relationship Alchemy', 'Conscious Conflict', 'Deeper Intimacy', 'Transformation'],
    whatThisReflects: 'Relationship alchemy is the art of using challenges as catalysts for deeper connection.',
    healingInvitation: 'Ask: How can this relationship challenge become an opportunity for deeper connection?',
    affirmation: 'I transform relationship challenges into deeper connection. Conflict becomes intimacy.',
  },
  {
    id: 'fallback-39',
    title: 'The Authentic Connection',
    category: 'relational',
    message: "You're being called to show up more authentically in your relationships. This card invites you to let go of masks and performances.",
    keywords: ['Authenticity', 'Vulnerability', 'True Self', 'Connection'],
    whatThisReflects: 'Authentic connection requires the courage to be seen as we truly are.',
    healingInvitation: 'Ask: Where am I performing instead of being real? What would authentic connection look like?',
    affirmation: 'I show up authentically in my relationships. I am loved for who I truly am.',
  },
  {
    id: 'fallback-40',
    title: 'The Relationship Healer',
    category: 'relational',
    message: "A relationship is ready for healing. This card signals that repair is possible - but it requires conscious attention, honest communication, and a willingness to grow together.",
    keywords: ['Healing', 'Repair', 'Reconciliation', 'Growth'],
    whatThisReflects: 'Relationships can heal when both parties are willing to do the work. Sometimes the healing is reconciliation; sometimes it\'s release.',
    healingInvitation: 'Ask: What relationship is asking for healing attention? What\'s my part in the repair?',
    affirmation: 'I am capable of healing my relationships. Love can mend what was broken.',
  },
  {
    id: 'fallback-41',
    title: 'The Conscious Communication',
    category: 'relational',
    message: "How you speak and listen shapes your relationships. This card invites you to communicate with more presence, honesty, and compassion.",
    keywords: ['Communication', 'Presence', 'Honesty', 'Compassion'],
    whatThisReflects: 'Most relationship challenges are rooted in communication patterns. Conscious communication transforms conflict into connection.',
    healingInvitation: 'Ask: How can I communicate more consciously in my relationships? Practice listening without planning your response.',
    affirmation: 'I communicate with presence and compassion. My words create connection.',
  },
  {
    id: 'fallback-42',
    title: 'The Relationship with Self',
    category: 'relational',
    message: "Your most important relationship is the one you have with yourself. This card invites you to examine how you treat, talk to, and relate to yourself.",
    keywords: ['Self-Relationship', 'Self-Love', 'Inner Dialogue', 'Self-Care'],
    whatThisReflects: 'How we relate to ourselves becomes the template for how we relate to others and how we allow others to relate to us.',
    healingInvitation: 'Ask: How do I treat myself when no one is watching? Would I speak to a friend the way I speak to myself?',
    affirmation: 'I am in a loving relationship with myself. I treat myself with kindness and respect.',
  },
  {
    id: 'fallback-43',
    title: 'The Sacred Witness',
    category: 'relational',
    message: "Sometimes we just need to be seen - not fixed, not advised, just witnessed. This card invites you to offer or receive the gift of sacred witnessing.",
    keywords: ['Witnessing', 'Presence', 'Being Seen', 'Holding Space'],
    whatThisReflects: 'Being truly witnessed is one of the deepest healing experiences available. It requires no words, only presence.',
    healingInvitation: 'Ask: Who needs me to simply witness them today? Do I need to ask someone to witness me?',
    affirmation: 'I offer and receive the gift of sacred witnessing. Being seen is healing.',
  },
  {
    id: 'fallback-44',
    title: 'The Relational Field',
    category: 'relational',
    message: "Relationships are not just between individuals - they create fields of energy that affect everyone within them. This card invites you to sense the relational field you're creating.",
    keywords: ['Relational Field', 'Energy', 'Collective', 'Influence'],
    whatThisReflects: 'We don\'t just have relationships - we are in fields of relationship that shape our consciousness and well-being.',
    healingInvitation: 'Ask: What energy am I bringing to my relationships? What kind of relational field do I want to create?',
    affirmation: 'I consciously create loving relational fields. My presence uplifts those around me.',
  },

  // THE SOULSTREAM - Spiritual (11 cards)
  {
    id: 'fallback-45',
    title: 'The Quiet Pull',
    category: 'spiritual',
    message: "There's something stirring beneath the surface - a quiet longing, a nudge, a whisper. You may not know where it's leading, only that it's time to follow.",
    keywords: ['Soul Calling', 'Inner Guidance', 'Surrender', 'Trust'],
    whatThisReflects: 'Soul callings often begin as subtle feelings rather than clear visions. The soul speaks through longing, synchronicity, and inexplicable attractions.',
    healingInvitation: 'Ask: What am I being called toward, even if I don\'t understand why? Take one small step in that direction.',
    affirmation: 'I trust the quiet pull of my soul. I follow what calls to me.',
  },
  {
    id: 'fallback-46',
    title: 'The Soul Retrieval',
    category: 'spiritual',
    message: "A part of you that was lost, hidden, or given away is ready to return. This card signals soul retrieval - the coming home of fragmented aspects of yourself.",
    keywords: ['Soul Retrieval', 'Wholeness', 'Integration', 'Coming Home'],
    whatThisReflects: 'When we experience trauma or disconnection, parts of our soul can fragment or hide. Healing involves calling these parts home.',
    healingInvitation: 'Ask: What part of me is ready to come home? Welcome it with open arms.',
    affirmation: 'I welcome all parts of myself home. I am becoming whole.',
  },
  {
    id: 'fallback-47',
    title: 'The Divine Masculine',
    category: 'spiritual',
    message: "The active, protective, directional aspect of your nature is asking for expression. This card invites you to honour the wisdom that comes through focused action and healthy assertion.",
    keywords: ['Divine Masculine', 'Action', 'Protection', 'Direction'],
    whatThisReflects: 'The Divine Masculine represents the active principle in all beings, regardless of gender. It\'s the energy of protection, direction, and purposeful action.',
    healingInvitation: 'Ask: How can I honour the active, directional aspects of my nature? Take one decisive action today.',
    affirmation: 'I honour the divine masculine within me. I act with purpose and protection.',
  },
  {
    id: 'fallback-48',
    title: 'The Divine Feminine',
    category: 'spiritual',
    message: "The receptive, intuitive, flowing aspect of your nature is asking for acknowledgment. This card invites you to honour the wisdom that comes through feeling, being, and allowing.",
    keywords: ['Divine Feminine', 'Receptivity', 'Intuition', 'Being'],
    whatThisReflects: 'The Divine Feminine represents the receptive principle in all beings, regardless of gender. It\'s the energy of intuition, creativity, and emotional intelligence.',
    healingInvitation: 'Ask: How can I honour the receptive, intuitive aspects of my nature? Practice being rather than doing for a period today.',
    affirmation: 'I honour the divine feminine within me. I receive wisdom through intuition and feeling.',
  },
  {
    id: 'fallback-49',
    title: 'The Ancestral Healing',
    category: 'spiritual',
    message: "You're not just healing for yourself - you're healing generational patterns that flow through your lineage. This card signals that your personal transformation contributes to ancestral healing.",
    keywords: ['Ancestral Healing', 'Generational Patterns', 'Lineage', 'Legacy'],
    whatThisReflects: 'Ancestral healing recognises that we carry both gifts and wounds from our lineage. When we heal personal patterns, we often heal ancestral patterns too.',
    healingInvitation: 'Ask: What pattern am I healing that may also heal my ancestral line? Send gratitude to your ancestors.',
    affirmation: 'I heal for myself and my lineage. My transformation breaks generational cycles.',
  },
  {
    id: 'fallback-50',
    title: 'The Synchronicity',
    category: 'spiritual',
    message: "The universe is winking at you. This card signals meaningful coincidence - those moments when inner and outer worlds align in ways that feel like magic.",
    keywords: ['Synchronicity', 'Meaningful Coincidence', 'Universe', 'Signs'],
    whatThisReflects: 'Synchronicities are the universe\'s way of communicating. They invite us to pay attention and trust the larger intelligence at work.',
    healingInvitation: 'Ask: What synchronicities have I noticed lately? What might they be telling me?',
    affirmation: 'I notice and trust synchronicities. The universe communicates with me through meaningful coincidence.',
  },
  {
    id: 'fallback-51',
    title: 'The Sacred Purpose',
    category: 'spiritual',
    message: "You have a unique contribution to make to the world. This card invites you to connect with your sacred purpose - the reason your soul chose this life.",
    keywords: ['Purpose', 'Soul Mission', 'Contribution', 'Meaning'],
    whatThisReflects: 'Purpose isn\'t something we find - it\'s something we remember. It\'s encoded in our soul before birth.',
    healingInvitation: 'Ask: What contribution is my soul here to make? What brings me alive in a way that also serves others?',
    affirmation: 'I am aligned with my sacred purpose. My life has deep meaning.',
  },
  {
    id: 'fallback-52',
    title: 'The Spiritual Practice',
    category: 'spiritual',
    message: "Your soul is asking for regular nourishment. This card invites you to deepen or establish a spiritual practice that keeps you connected to Source.",
    keywords: ['Spiritual Practice', 'Devotion', 'Connection', 'Ritual'],
    whatThisReflects: 'Spiritual practice is how we maintain connection with the divine. It\'s not about perfection - it\'s about showing up.',
    healingInvitation: 'Ask: What spiritual practice is calling me? Commit to even 5 minutes of daily practice.',
    affirmation: 'I nourish my soul through regular spiritual practice. I stay connected to Source.',
  },
  {
    id: 'fallback-53',
    title: 'The Expanded Consciousness',
    category: 'spiritual',
    message: "Your awareness is expanding beyond ordinary perception. This card signals a time of spiritual opening - new dimensions of consciousness becoming available.",
    keywords: ['Expanded Consciousness', 'Awakening', 'Perception', 'Dimensions'],
    whatThisReflects: 'Consciousness expansion is a natural part of spiritual evolution. It often comes in waves, with integration periods in between.',
    healingInvitation: 'Ask: What new perceptions or awarenesses are emerging? How can I integrate them gracefully?',
    affirmation: 'My consciousness is expanding. I welcome new dimensions of awareness.',
  },
  {
    id: 'fallback-54',
    title: 'The Surrender',
    category: 'spiritual',
    message: "Control has served you, but now it's time to release. This card invites deep surrender - not giving up, but giving over. Trust a larger intelligence to guide you.",
    keywords: ['Surrender', 'Trust', 'Release', 'Divine Will'],
    whatThisReflects: 'Surrender is one of the most powerful spiritual practices. It\'s the recognition that we are not in control - and that this is actually a relief.',
    healingInvitation: 'Ask: What am I trying to control that is asking to be surrendered? Practice release through breath and intention.',
    affirmation: 'I surrender to a larger intelligence. I trust the divine plan for my life.',
  },
  {
    id: 'fallback-55',
    title: 'The Unity Consciousness',
    category: 'spiritual',
    message: "You are not separate. You are connected to all life, all beings, all dimensions. This card signals the experience or recognition of fundamental unity.",
    keywords: ['Unity', 'Oneness', 'Connection', 'Interconnection'],
    whatThisReflects: 'Unity consciousness is the recognition that separation is an illusion. We are all expressions of one divine consciousness.',
    healingInvitation: 'Ask: Where do I feel my connection to the whole? Practice recognising the divine in all beings.',
    affirmation: 'I am one with all life. Separation is an illusion. I am connected to everything.',
  },
];
